import { toggleMark, setBlockType, wrapIn } from "prosemirror-commands";
import { undo, redo } from "prosemirror-history";
import { wrapInList } from "prosemirror-schema-list";
import { useEditorEventCallback } from "@handlewithcare/react-prosemirror";
import { Button } from "@/components/ui/button";
import {
  Bold,
  Italic,
  Code,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Undo,
  Redo,
  FileCode,
  Workflow,
  ImagePlus,
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useRef } from "react";

interface SimpleEditorMenuProps {
  onOpenFlowchart?: (source: string | undefined, pos: number | null) => void;
  onUploadImage?: (file: File) => Promise<string>;
}

export default function SimpleEditorMenu({ onOpenFlowchart, onUploadImage }: SimpleEditorMenuProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toggleBold = useEditorEventCallback((view) => {
    const toggleBoldMark = toggleMark(view.state.schema.marks["strong"]);
    toggleBoldMark(view.state, view.dispatch, view);
    view.focus();
  });

  const toggleItalic = useEditorEventCallback((view) => {
    const toggleItalicMark = toggleMark(view.state.schema.marks["em"]);
    toggleItalicMark(view.state, view.dispatch, view);
    view.focus();
  });

  const toggleCode = useEditorEventCallback((view) => {
    const toggleCodeMark = toggleMark(view.state.schema.marks["code"]);
    toggleCodeMark(view.state, view.dispatch, view);
    view.focus();
  });

  const handleUndo = useEditorEventCallback((view) => {
    undo(view.state, view.dispatch, view);
    view.focus();
  });

  const handleRedo = useEditorEventCallback((view) => {
    redo(view.state, view.dispatch, view);
    view.focus();
  });

  const setHeading1 = useEditorEventCallback((view) => {
    const setHeading = setBlockType(view.state.schema.nodes.heading, { level: 1 });
    setHeading(view.state, view.dispatch, view);
    view.focus();
  });

  const setHeading2 = useEditorEventCallback((view) => {
    const setHeading = setBlockType(view.state.schema.nodes.heading, { level: 2 });
    setHeading(view.state, view.dispatch, view);
    view.focus();
  });

  const setHeading3 = useEditorEventCallback((view) => {
    const setHeading = setBlockType(view.state.schema.nodes.heading, { level: 3 });
    setHeading(view.state, view.dispatch, view);
    view.focus();
  });

  const toggleBulletList = useEditorEventCallback((view) => {
    const wrapList = wrapInList(view.state.schema.nodes.bullet_list);
    wrapList(view.state, view.dispatch, view);
    view.focus();
  });

  const toggleOrderedList = useEditorEventCallback((view) => {
    const wrapList = wrapInList(view.state.schema.nodes.ordered_list);
    wrapList(view.state, view.dispatch, view);
    view.focus();
  });

  const toggleBlockquote = useEditorEventCallback((view) => {
    const wrapQuote = wrapIn(view.state.schema.nodes.blockquote);
    wrapQuote(view.state, view.dispatch, view);
    view.focus();
  });

  const setCodeBlock = useEditorEventCallback((view) => {
    const setCode = setBlockType(view.state.schema.nodes.code_block);
    setCode(view.state, view.dispatch, view);
    view.focus();
  });

  // Open the visual flowchart editor. If cursor is inside an existing mermaid
  // code block, load its content for editing; otherwise start fresh.
  const openFlowchartEditor = useEditorEventCallback((view) => {
    const { state } = view;
    const { $from } = state.selection;

    // Check if we're inside a mermaid code_block
    const codeBlockType = state.schema.nodes.code_block;
    for (let depth = $from.depth; depth > 0; depth--) {
      const node = $from.node(depth);
      if (node.type === codeBlockType && node.attrs.params === "mermaid") {
        onOpenFlowchart?.(node.textContent, $from.before(depth));
        return;
      }
    }

    // Not inside a mermaid block — create new
    onOpenFlowchart?.(undefined, null);
  });

  const insertImage = useEditorEventCallback((view, url: string, alt: string) => {
    const { schema } = view.state;
    const imageNode = schema.nodes.image;
    if (!imageNode) return;
    const node = imageNode.create({ src: url, alt });
    const tr = view.state.tr.replaceSelectionWith(node);
    view.dispatch(tr);
    view.focus();
  });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onUploadImage) return;
    try {
      const url = await onUploadImage(file);
      insertImage(url, file.name);
    } catch (err) {
      console.error("Image upload failed:", err);
    }
    // Reset input so the same file can be re-selected
    e.target.value = "";
  };

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b bg-muted/50">
      <Button type="button" variant="ghost" size="sm" onClick={toggleBold} title="Bold (Ctrl+B)">
        <Bold className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={toggleItalic} title="Italic (Ctrl+I)">
        <Italic className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={toggleCode} title="Inline Code (Ctrl+Shift+C)">
        <Code className="h-4 w-4" />
      </Button>

      <Separator orientation="vertical" className="h-8" />

      <Button type="button" variant="ghost" size="sm" onClick={setHeading1} title="Heading 1">
        <Heading1 className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={setHeading2} title="Heading 2">
        <Heading2 className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={setHeading3} title="Heading 3">
        <Heading3 className="h-4 w-4" />
      </Button>

      <Separator orientation="vertical" className="h-8" />

      <Button type="button" variant="ghost" size="sm" onClick={toggleBulletList} title="Bullet List">
        <List className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={toggleOrderedList} title="Numbered List">
        <ListOrdered className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={toggleBlockquote} title="Quote">
        <Quote className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={setCodeBlock} title="Code Block">
        <FileCode className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={openFlowchartEditor} title="Insert Flowchart">
        <Workflow className="h-4 w-4" />
      </Button>
      {onUploadImage && (
        <>
          <Button type="button" variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} title="Insert Image">
            <ImagePlus className="h-4 w-4" />
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={handleFileSelect}
          />
        </>
      )}

      <div className="flex-1" />

      <Button type="button" variant="ghost" size="sm" onClick={handleUndo} title="Undo (Ctrl+Z)">
        <Undo className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={handleRedo} title="Redo (Ctrl+Y)">
        <Redo className="h-4 w-4" />
      </Button>
    </div>
  );
}
